import {
  Badge,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popover
} from '@material-ui/core'
import { BellIcon } from 'lucide-react'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import AnnouncementsPopover from '../../components/AnnouncementsPopover'
import ConnectionAnnouncementModal from '../../components/ConnectionAnnouncementModal'
import { AuthContext } from '../../context/Auth/AuthContext'
import { WhatsAppsContext } from '../../context/WhatsApp/WhatsAppsContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import QrcodeModal from '../QrcodeModal'

import useSound from 'use-sound'
import notifySound from '../../assets/chat_notify.mp3'
import alertSound from '../../assets/sound.mp3'
import NotificationsPopOver from '../NotificationsPopOver'
import TaskCommentsModal from '../TaskCommentsModal'
import TaskModal from '../TaskModal'
import TasksPopOver from '../TasksPopOver'
import { useStyles } from './style'

import { SocketContext } from '../../context/Socket/SocketContext'
import useSettings from '../../hooks/useSettings'
import { getSafeAvatarUrl } from '../../helpers/avatarFallback'
import { resolveNotificationSoundUrls } from '../../utils/notificationSounds'
import ChatPopover from '../ChatPopover'

// Função utilitária para verificar se o horário atual está dentro do intervalo permitido
const isWithinNotificationPeriod = (startTime, endTime) => {
  if (!startTime || !endTime) return true // Se não há configuração, permite notificações

  const now = new Date()
  const currentTime =
    now.getHours().toString().padStart(2, '0') +
    ':' +
    now.getMinutes().toString().padStart(2, '0')

  // Se o horário de início é menor que o de fim (mesmo dia)
  if (startTime <= endTime) {
    return currentTime >= startTime && currentTime <= endTime
  }
  // Se o horário passa da meia-noite (ex: 22:00 até 06:00)
  else {
    return currentTime >= startTime || currentTime <= endTime
  }
}

const LOG_THEMES = {
  ticket:
    'background: #0284c7; color: #fff; padding: 2px 4px; border-radius: 2px; font-weight: bold;',
  message:
    'background: #16a34a; color: #fff; padding: 2px 4px; border-radius: 2px; font-weight: bold;',
  task: 'background: #ca8a04; color: #fff; padding: 2px 4px; border-radius: 2px; font-weight: bold;',
  announcement:
    'background: #9333ea; color: #fff; padding: 2px 4px; border-radius: 2px; font-weight: bold;',
  system:
    'background: #4b5563; color: #fff; padding: 2px 4px; border-radius: 2px; font-weight: bold;',
  error:
    'background: #dc2626; color: #fff; padding: 2px 4px; border-radius: 2px; font-weight: bold;'
}

export default function NotificationsCenter({ handleChatOpen }) {
  const classes = useStyles()
  const { user } = useContext(AuthContext)
  const socketManager = useContext(SocketContext)
  const location = useLocation()

  const [anchorEl, setAnchorEl] = useState(null)
  const [tickets, setTickets] = useState([])
  const [chats, setChats] = useState([])
  const [tasks, setTasks] = useState([])
  const [announcements, setAnnouncements] = useState([])
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState(null)
  const [taskCommentsModalOpen, setTaskCommentsModalOpen] = useState(false)
  const [selectedTaskForComments, setSelectedTaskForComments] = useState(null)
  const [totalUnreadCount, setTotalUnreadCount] = useState(0)
  const [volume] = useState(localStorage.getItem('volume') || 1)
  const [savedAnnouncements, setSavedAnnouncements] = useState([])
  const [connectionAnnouncementModalOpen, setConnectionAnnouncementModalOpen] =
    useState(false)
  const [connectionAnnouncement, setConnectionAnnouncement] = useState(null)
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [selectedWhatsAppId, setSelectedWhatsAppId] = useState(null)

  const { whatsApps, loading } = useContext(WhatsAppsContext)

  const audioEnabledRef = useRef(true)

  const { getSetting } = useSettings()
  const [soundGroupNotifications, setSoundGroupNotifications] = useState(null)
  const [notificationStartTime, setNotificationStartTime] = useState('')
  const [notificationEndTime, setNotificationEndTime] = useState('')
  const [soundUrls, setSoundUrls] = useState({
    attendanceMessageSoundUrl: null,
    groupMessageSoundUrl: null
  })

  const attendanceSound = soundUrls.attendanceMessageSoundUrl || alertSound
  const groupSound = soundUrls.groupMessageSoundUrl || attendanceSound

  const [playAttendanceAlert] = useSound(attendanceSound, {
    volume: parseFloat(volume),
    preload: true
  })
  const [playGroupAlert] = useSound(groupSound, {
    volume: parseFloat(volume),
    preload: true
  })
  const [playNotify] = useSound(notifySound, {
    volume: parseFloat(volume),
    preload: true
  })
  const soundAttendanceAlertRef = useRef(playAttendanceAlert)
  const soundGroupAlertRef = useRef(playGroupAlert)
  const soundNotifyRef = useRef(playNotify)
  const processedPendingNotificationKeysRef = useRef(new Map())
  const lastVibrationAtRef = useRef(0)

  const [isNotificationExcludeTickets, setIsNotificationExcludeTickets] =
    useState('')
  const excludeTicketsRef = useRef(isNotificationExcludeTickets)

  const triggerNotificationVibration = useCallback(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return
    }

    if (typeof navigator.vibrate !== 'function') {
      return
    }

    const now = Date.now()

    // Evita disparos muito próximos quando vários eventos chegam em sequência.
    if (now - lastVibrationAtRef.current < 800) {
      return
    }

    navigator.vibrate([200, 120, 200])
    lastVibrationAtRef.current = now
  }, [])

  const shouldPlayNotificationSound = ticket => {
    if (!ticket) return true // Toca o som por padrão se o ticket não for fornecido

    const isChatbot = ticket.chatbot === true
    const notificationDisabled = ticket.queue?.notificationact === true
    const isUnassigned = !ticket.userId

    if (isChatbot || (isUnassigned && notificationDisabled)) {
      return false
    }
    return true
  }

  const userHasAccessToPendingTicket = useCallback(
    ticket => {
      if (!ticket || !user) return false

      if (user.super || user.profile === 'admin') {
        return true
      }

      if (ticket.userId && ticket.userId === user.id) {
        return true
      }

      if (!ticket.queueId || !user.queues?.length) {
        return false
      }

      return user.queues.some(queue => queue.id === ticket.queueId)
    },
    [user]
  )

  const getPendingNotificationKey = useCallback(data => {
    const ticketId = data?.ticket?.id

    if (!ticketId) {
      return null
    }

    return [
      ticketId,
      data?.message?.notificationCount,
      data?.ticket?.notificationCount,
      data?.ticket?.notificationCycleKey,
      data?.message?.timestamp
    ]
      .filter(Boolean)
      .join('-')
  }, [])

  const getSavedAnnouncements = () => {
    const announcements = localStorage.getItem('announcements')
    return announcements ? JSON.parse(announcements) : []
  }

  const saveAnnouncementToLocalStorage = (announcementId, userId) => {
    const announcements = getSavedAnnouncements()
    const newAnnouncement = { announcementId, userId }
    if (
      !announcements.some(
        a => a.announcementId === announcementId && a.userId === userId
      )
    ) {
      announcements.push(newAnnouncement)
      localStorage.setItem('announcements', JSON.stringify(announcements))
      setSavedAnnouncements(announcements)
    }
  }

  const fetchInitialData = useCallback(async () => {
    try {
      const [ticketsResponse, announcementsResponse] = await Promise.all([
        api.get('/tickets/', {
          params: {
            withUnreadMessages: 'true',
            status: 'open, pending'
            // isGroup: 'false'
          }
        }),
        api.get('/announcements/')
      ])

      const activeTickets = (ticketsResponse.data.records || []).filter(
        ticket =>
          (ticket.status === 'open' || ticket.status === 'pending') &&
          // !ticket.isGroup &&
          // ticket.isGroup && //alterado para notificar grupos
          !ticket.chatbot
      )

      setTickets(activeTickets)
      setChats([])

      const loadedAnnouncements = announcementsResponse.data.records || []

      const savedAnnouncementsIds = getSavedAnnouncements()
        .filter(announcement => announcement.userId === user.id)
        .map(announcement => announcement.announcementId)

      const unreadAnnouncements = loadedAnnouncements.filter(announcement => {
        if (announcement.tipo === 'storage-alert') {
          return announcement.status === true
        }

        return (
          !savedAnnouncementsIds.includes(announcement.id) &&
          !announcement.isConnectionAnnouncement
        )
      })
      setAnnouncements(unreadAnnouncements)

      const connectionAnn = loadedAnnouncements.find(
        ann => ann.isConnectionAnnouncement
      )

      if (connectionAnn && location.pathname !== '/connections' && !loading) {
        const whatsAppName = connectionAnn.title
          .replace('Conexão ', '')
          .replace(' Desconectada', '')
        const whatsApp = whatsApps.find(w => w.name === whatsAppName)

        if (whatsApp && whatsApp.status === 'CONNECTED') {
          setConnectionAnnouncementModalOpen(false)
        } else if (whatsApp) {
          const hiddenIds = user?.hiddenConnectionAnnouncementIds || []
          const hiddenFromBackend = hiddenIds.includes(whatsApp.id)
          const hiddenFromStorage =
            localStorage.getItem(
              `doNotShowConnectionAnnouncement_${whatsApp.id}`
            ) === 'true'

          if (!hiddenFromBackend && !hiddenFromStorage) {
            setConnectionAnnouncement(connectionAnn)
            setSelectedWhatsAppId(whatsApp.id)
            setConnectionAnnouncementModalOpen(true)
          } else {
            setConnectionAnnouncementModalOpen(false)
          }
        } else {
          setConnectionAnnouncementModalOpen(false)
        }
      } else {
        setConnectionAnnouncementModalOpen(false)
      }
    } catch (err) {
      // Ignorar erros 401 que ocorrem durante logout
      if (err?.response?.status !== 401) {
        console.error('Error fetching notifications:', err)
      }
    }
  }, [
    loading,
    location.pathname,
    user.id,
    user?.hiddenConnectionAnnouncementIds,
    whatsApps
  ])

  useEffect(() => {
    excludeTicketsRef.current = isNotificationExcludeTickets
  }, [isNotificationExcludeTickets])

  // useEffect(() => {
  //   const handleStorageNotification = event => {
  //     const excludeValue = Number(excludeTicketsRef.current)

  //     if (!isNaN(excludeValue) && excludeValue > 0) {
  //       console.log(
  //         '%c[STORAGE-ALERT]%c Ignorado - A regra de exclusão automática está ativa (excludeClosedTicketsAfter > 0)',
  //         LOG_THEMES.system,
  //         'color: #94a3b8;'
  //       )
  //       return
  //     }

  //     const { message, usedGB, timestamp } = event.detail

  //     console.log(
  //       `%c[STORAGE-ALERT]%c Processando alerta de armazenamento. Uso atual: ${usedGB}GB (Timestamp: ${timestamp})`,
  //       LOG_THEMES.task,
  //       'color: #eab308; font-weight: bold;'
  //     )

  //     // Adiciona como uma notificação de "Tarefa" ou similar para aparecer na lista
  //     setTasks(prev => {
  //       const id = `storage-${usedGB}-${timestamp}`
  //       if (prev.some(t => t.id === id)) {
  //         console.log(
  //           '%c[STORAGE-ALERT]%c Duplicata evitada - Alerta com este ID já existe no estado',
  //           LOG_THEMES.task,
  //           'color: #64748b;'
  //         )
  //         return prev
  //       }

  //       const storageTask = {
  //         id,
  //         title: 'Alerta de Sistema',
  //         description: message,
  //         _isStorageAlert: true // Flag para estilização customizada
  //       }

  //       console.log(
  //         '%c[STORAGE-ALERT]%c Sucesso - Alerta de armazenamento injetado no painel de tarefas',
  //         LOG_THEMES.task,
  //         'color: #22c55e;'
  //       )

  //       return [storageTask, ...prev]
  //     })

  //     // Dispara vibração e som de notificação padrão apenas se não estiver silenciado pela regra
  //     triggerNotificationVibration()
  //     if (audioEnabledRef.current) {
  //       try {
  //         console.log(
  //           '%c[AUDIO]%c Emitindo som de notificação para o Alerta de Sistema',
  //           LOG_THEMES.system,
  //           'color: #0ea5e9;'
  //         )
  //         soundNotifyRef.current()
  //       } catch (error) {
  //         console.error('%c[AUDIO-ERROR]', LOG_THEMES.error, error)
  //       }
  //     }
  //   }

  //   window.addEventListener(
  //     'storage-threshold-reached',
  //     handleStorageNotification
  //   )

  //   return () => {
  //     window.removeEventListener(
  //       'storage-threshold-reached',
  //       handleStorageNotification
  //     )
  //   }
  // }, [triggerNotificationVibration])

  useEffect(() => {
    const handleStorageNotification = async event => {
      const excludeValue = Number(excludeTicketsRef.current)

      if (!isNaN(excludeValue) && excludeValue > 0) {
        console.log(
          '%c[STORAGE-ALERT]%c Ignorado - A regra de exclusão automática está ativa (excludeClosedTicketsAfter > 0)',
          LOG_THEMES.system,
          'color: #94a3b8;'
        )
        return
      }

      const { message, usedGB, timestamp } = event.detail
      const roundedGB = Math.floor(usedGB)
      const storageKey = `storage_alert_notified_${user?.id || 'guest'}_${roundedGB}`

      if (localStorage.getItem(storageKey) === 'true') {
        return
      }

      const companyId = localStorage.getItem('companyId')

      try {
        const { data: responseAnnouncements } = await api.get(
          '/announcements/',
          {
            params: { companyId }
          }
        )
        const announcementsList =
          responseAnnouncements.records || responseAnnouncements || []

        // Verifica se já existe um anúncio deste patamar no banco (mesmo que inativo/lido por outros)
        const existingAlert = announcementsList.find(
          ann =>
            ann.tipo === 'storage-alert' &&
            (ann.text?.includes(`${roundedGB}GB`) ||
              ann.title?.includes(`${roundedGB}GB`))
        )

        if (existingAlert) {
          // Se o anúncio existe mas está inativo ou já foi lido, apenas marcamos no LocalStorage do usuário atual
          localStorage.setItem(storageKey, 'true')
          return
        }

        // Cria o anúncio apenas se ele realmente nunca existiu no banco para este patamar
        await api.post('/announcements/', {
          title: `Alerta de Sistema - ${roundedGB}GB Atingido`,
          text: message,
          priority: 3,
          status: true,
          companyId: companyId ? Number(companyId) : null,
          tipo: 'storage-alert'
        })

        localStorage.setItem(storageKey, 'true')

        triggerNotificationVibration()
        if (audioEnabledRef.current) {
          try {
            soundNotifyRef.current()
          } catch (error) {
            console.error('%c[AUDIO-ERROR]', LOG_THEMES.error, error)
          }
        }
      } catch (error) {
        console.error('%c[STORAGE-ALERT-ERROR]', LOG_THEMES.error, error)
      }
    }

    window.addEventListener(
      'storage-threshold-reached',
      handleStorageNotification
    )

    return () => {
      window.removeEventListener(
        'storage-threshold-reached',
        handleStorageNotification
      )
    }
  }, [triggerNotificationVibration, user?.id])

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [soundSetting, startTimeSetting, endTimeSetting, excludeTickets] =
          await Promise.all([
            getSetting('soundGroupNotifications', 'disabled'),
            getSetting('notificationResetStartDate', ''),
            getSetting('notificationResetEndDate', ''),
            getSetting('excludeClosedTicketsAfter', '')
          ])

        setSoundGroupNotifications(soundSetting === 'enabled')
        setNotificationStartTime(startTimeSetting)
        setNotificationEndTime(endTimeSetting)
        setIsNotificationExcludeTickets(excludeTickets)
      } catch (error) {
        // Ignorar erros 401 que ocorrem durante logout
        if (error?.response?.status !== 401) {
          console.error('Error fetching notification settings:', error)
        }
        setSoundGroupNotifications(false)
        setNotificationStartTime('')
        setNotificationEndTime('')
      }
    }
    fetchSettings()
  }, [getSetting])

  useEffect(() => {
    const loadedAnnouncements = getSavedAnnouncements()
    setSavedAnnouncements(loadedAnnouncements)
  }, [])

  useEffect(() => {
    soundAttendanceAlertRef.current = playAttendanceAlert
    soundGroupAlertRef.current = playGroupAlert
    soundNotifyRef.current = playNotify

    if (!('Notification' in window)) {
      console.log("This browser doesn't support notifications")
    } else {
      Notification.requestPermission()
    }
  }, [playAttendanceAlert, playGroupAlert, playNotify])

  useEffect(() => {
    const fetchNotificationSounds = async () => {
      try {
        const { data } = await api.get('/notification-sounds')
        const urls = resolveNotificationSoundUrls(
          data?.sounds || [],
          data?.preferences || {}
        )

        setSoundUrls({
          attendanceMessageSoundUrl: urls.attendanceMessageSoundUrl,
          groupMessageSoundUrl: urls.groupMessageSoundUrl
        })
      } catch (error) {
        if (error?.response?.status !== 401) {
          console.error('Error fetching notification sounds:', error)
        }
      }
    }

    fetchNotificationSounds()
  }, [user.id])

  useEffect(() => {
    fetchInitialData()
  }, [fetchInitialData, savedAnnouncements, user.id, location])

  const isUserInTicketQueue = useCallback(
    ticket => {
      if (!ticket || !user) return false
      if (!ticket.queueId) return false
      return user.queues?.some(queue => queue.id === ticket.queueId) || false
    },
    [user]
  )

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    const onConnectNotificationsPopover = () => {
      socket.emit('joinNotification')
    }
    socketManager.onConnect(onConnectNotificationsPopover)

    const handleInternalChatMessageNotification = data => {
      if (!data || data.senderId === user.id) return

      const isCurrentActiveChat =
        location.pathname.includes('/chats') &&
        location.pathname.endsWith(data.chatUuid || '')

      if (isCurrentActiveChat) return

      console.log(
        `%c[INTERNAL-CHAT]%c Nova mensagem interna recebida de ${data.sender?.name || 'Usuário'}`,
        LOG_THEMES.message,
        'color: #06b6d4; font-weight: bold;'
      )

      triggerNotificationVibration()

      // Adiciona a mensagem/chat na lista flutuante de chats/tickets ou cria uma estrutura equivalente
      setChats(prevChats => {
        const chatExists = prevChats.find(c => c.id === data.chatId)
        if (chatExists) {
          return prevChats.map(c =>
            c.id === data.chatId
              ? {
                  ...c,
                  lastMessage: data.message,
                  updatedAt: new Date().toISOString()
                }
              : c
          )
        }
        return [
          {
            id: data.chatId,
            uuid: data.chatUuid,
            title: data.chatTitle || data.sender?.name || 'Chat Interno',
            lastMessage: data.message,
            updatedAt: new Date().toISOString(),
            isInternalChat: true
          },
          ...prevChats
        ]
      })

      if (audioEnabledRef.current) {
        try {
          soundNotifyRef.current()
        } catch (error) {
          console.error('%c[AUDIO-ERROR]', LOG_THEMES.error, error)
        }
      }
    }

    // Registra o evento do socket para o chat interno da empresa
    socket.on(`company-${companyId}-chat`, data => {
      if (data.action === 'new-message' || data.action === 'create') {
        handleInternalChatMessageNotification(data.newMessage || data)
      }
    })

    const handlePendingTicketNotification = data => {
      const notificationKey = getPendingNotificationKey(data)

      if (notificationKey) {
        const now = Date.now()
        const processedAt =
          processedPendingNotificationKeysRef.current.get(notificationKey)

        if (processedAt && now - processedAt < 15000) {
          console.log(
            '%c[TICKET-PENDING]%c Debounce Ativo - Notificação idêntica travada por 15s para evitar flood de áudio',
            LOG_THEMES.ticket,
            'color: #f59e0b;'
          )
          return
        }

        processedPendingNotificationKeysRef.current.set(notificationKey, now)

        setTimeout(() => {
          processedPendingNotificationKeysRef.current.delete(notificationKey)
        }, 15000)
      }

      if (!user?.foco) {
        console.log(
          '%c[TICKET-PENDING]%c Bloqueado - Usuário não possui foco ativo (foco: false)',
          LOG_THEMES.ticket,
          'color: #94a3b8;'
        )
        return
      }

      const isUnassigned = !data.ticket?.userId
      const isMyQueue = isUserInTicketQueue(data.ticket)
      const isMyTicket = data.ticket?.userId === user.id
      const isAdmin = user.super || user.profile === 'admin'

      const isForCurrentUser =
        isMyTicket || (isUnassigned && isMyQueue) || isAdmin

      const isWithinPeriod = isWithinNotificationPeriod(
        notificationStartTime,
        notificationEndTime
      )

      if (isForCurrentUser && isWithinPeriod) {
        console.log(
          `%c[TICKET-PENDING]%c Sucesso - Ticket ID: ${data.ticket?.id} atende aos requisitos. Motivo: [Dono: ${isMyTicket} | Fila Válida: ${isMyQueue} | Admin: ${isAdmin}] dentro do horário permitido`,
          LOG_THEMES.ticket,
          'color: #0284c7; font-weight: bold;'
        )
        triggerNotificationVibration()

        const notificationTimestamp =
          data.message?.timestamp || new Date().toISOString()
        const notificationTag = `pending-ticket-${data.ticket.id}-${new Date(
          notificationTimestamp
        ).getTime()}`

        if ('Notification' in window && Notification.permission === 'granted') {
          console.log(
            '%c[WEB-NOTIFICATION]%c Disparando banner nativo no Sistema Operacional',
            LOG_THEMES.system,
            'color: #38bdf8;'
          )
          const notification = new Notification('Ticket Pendente', {
            body: `${
              data.message?.content || `Ticket #${data.ticket.id} está pendente`
            } - ${data.ticket?.contact?.name || 'Contato não identificado'}`,
            icon: getSafeAvatarUrl(data.ticket?.contact?.profilePicUrl),
            tag: notificationTag,
            vibrate: [300, 150, 300, 150, 600],
            renotify: true,
            silent: false
          })

          notification.onclick = function () {
            window.focus()
            if (data.ticket?.uuid) {
              window.location.href = `/tickets/${data.ticket.uuid}`
            }
            notification.close()
          }
        }

        setTickets(prevTickets => {
          const nextTicket = {
            ...data.ticket,
            notificationCount:
              data.message?.notificationCount || data.ticket?.notificationCount,
            notificationTimestamp
          }

          return [
            nextTicket,
            ...prevTickets.filter(ticket => ticket.id !== data.ticket.id)
          ]
        })

        if (
          audioEnabledRef.current &&
          shouldPlayNotificationSound(data.ticket)
        ) {
          try {
            console.log(
              '%c[AUDIO]%c Emitindo som: Ticket Pendente na fila/atribuído',
              LOG_THEMES.system,
              'color: #0ea5e9;'
            )
            soundAttendanceAlertRef.current()
          } catch (error) {
            console.error('%c[AUDIO-ERROR]', LOG_THEMES.error, error)
          }
        } else {
          console.log(
            '%c[AUDIO]%c Bloqueado - Áudio global desativado ou fila configurada como silenciosa/chatbot',
            LOG_THEMES.system,
            'color: #64748b;'
          )
        }
      } else {
        console.log(
          `%c[TICKET-PENDING]%c Descartado - Motivo: [Usuário Elegível: ${isForCurrentUser} | Horário Válido: ${isWithinPeriod}] (Ticket pertence a outra fila ou atendente)`,
          LOG_THEMES.ticket,
          'color: #64748b;'
        )
      }
    }

    const handleLocalPendingTicketNotification = event => {
      if (event?.detail?.action !== 'pending-ticket-notification') return
      handlePendingTicketNotification(event.detail)
    }

    const handleTicketNotification = data => {
      if (
        data.action === 'updateUnread' ||
        data.action === 'delete' ||
        (data.action === 'update' && data.chatbotEnded)
      ) {
        console.log(
          `%c[TICKET-ACTION]%c Removendo da lista local - Ticket ID: ${data.ticketId || data.ticket?.id} sofreu ação: ${data.action}`,
          LOG_THEMES.ticket,
          'color: #cbd5e1;'
        )
        setTickets(prevTickets =>
          prevTickets.filter(
            ticket => ticket.id !== (data.ticketId || data.ticket?.id)
          )
        )
        return
      }

      if (data.ticket && data.ticket.channel === 'internal') {
        console.log(
          '%c[TICKET-UPDATE]%c Ignorado - Canal interno descartado pelo gerenciador',
          LOG_THEMES.ticket,
          'color: #94a3b8;'
        )
        return
      }

      if (data.action === 'pending-ticket-notification') {
        handlePendingTicketNotification(data)
        return
      }

      if (data.action === 'update' && data.ticket) {
        const isMyTicket = data.ticket.userId === user.id
        const isUnassigned = !data.ticket.userId
        const isMyQueue = isUserInTicketQueue(data.ticket)
        const isOpenOrPending =
          data.ticket.status === 'pending' || data.ticket.status === 'open'

        if (isOpenOrPending && (isMyTicket || (isUnassigned && isMyQueue))) {
          if (!user?.foco) {
            console.log(
              '%c[TICKET-UPDATE]%c Atualização ignorada visualmente - Usuário sem foco ativo (foco: false)',
              LOG_THEMES.ticket,
              'color: #94a3b8;'
            )
            return
          }

          console.log(
            `%c[TICKET-UPDATE]%c Sucesso - Ticket ID: ${data.ticket.id} atualizado na lista. Motivo: [Status Aberto/Pendente: ${isOpenOrPending} | ${isMyTicket ? 'Meu Atendimento' : 'Minha Fila'}]`,
            LOG_THEMES.ticket,
            'color: #0ea5e9; font-weight: bold;'
          )

          setTickets(prevTickets => {
            const ticketExists = prevTickets.some(
              ticket => ticket.id === data.ticket.id
            )
            if (!ticketExists) {
              return [data.ticket, ...prevTickets]
            }
            return prevTickets.map(ticket =>
              ticket.id === data.ticket.id ? data.ticket : ticket
            )
          })

          if (
            audioEnabledRef.current &&
            shouldPlayNotificationSound(data.ticket)
          ) {
            try {
              console.log(
                '%c[AUDIO]%c Emitindo som: Atualização de ticket pendente/aberto',
                LOG_THEMES.system,
                'color: #0ea5e9;'
              )
              soundAttendanceAlertRef.current()
            } catch (error) {
              console.error('%c[AUDIO-ERROR]', LOG_THEMES.error, error)
            }
          } else {
            console.log(
              '%c[AUDIO]%c Bloqueado - Som indisponível para este ticket (chatbot ativo ou fila silenciada)',
              LOG_THEMES.system,
              'color: #64748b;'
            )
          }
        } else {
          console.log(
            `%c[TICKET-UPDATE]%c Limpando da Visão - Ticket ID: ${data.ticket.id} removido da lista local. Motivo: Não atende aos critérios [Status Elegível: ${isOpenOrPending} | Dono: ${isMyTicket} | Minha Fila: ${isMyQueue}]`,
            LOG_THEMES.ticket,
            'color: #4b5563;'
          )
          setTickets(prevTickets =>
            prevTickets.filter(ticket => ticket.id !== data.ticket.id)
          )
        }
      }
    }

    const handleMessageNotification = data => {
      if (data.message && data.message.channel === 'internal') {
        console.log(
          '%c[MESSAGE-NEW]%c Descartado - Mensagem de canal interno',
          LOG_THEMES.message,
          'color: #94a3b8;'
        )
        return
      }

      if (
        data.message?.ticket?.contact?.silencedFor?.users?.includes(user.id)
      ) {
        console.log(
          `%c[MESSAGE-NEW]%c Descartado - O cliente silenciou notificações para o seu usuário (id: ${user.id})`,
          LOG_THEMES.message,
          'color: #f59e0b;'
        )
        return
      }

      const isNewUnreadMessage = data.action === 'create' && !data.message?.read
      const isChatbot = data.message?.ticket?.chatbot === true
      const isTicketOpenOrPending =
        data.ticket?.status === 'open' || data.ticket?.status === 'pending'
      const isGroup = data.ticket?.isGroup

      const isMyTicket = data.ticket?.userId === user?.id
      const isUnassigned = !data.ticket?.userId
      const isMyQueue = isUserInTicketQueue(data.ticket)

      const isForCurrentUser = isMyTicket || (isUnassigned && isMyQueue)

      const shouldProcessNotification =
        isNewUnreadMessage &&
        isTicketOpenOrPending &&
        !isChatbot &&
        isForCurrentUser &&
        (!isGroup || (isGroup && soundGroupNotifications))

      if (shouldProcessNotification) {
        console.log(
          `%c[MESSAGE-NEW]%c Sucesso - Nova mensagem processada para o ticket ID: ${data.ticket.id}. Motivo: [Mensagem Nova: ${isNewUnreadMessage} | Aberto/Pendente: ${isTicketOpenOrPending} | Sem Chatbot: ${!isChatbot} | ${isMyTicket ? 'Meu Ticket' : 'Minha Fila'}]`,
          LOG_THEMES.message,
          'color: #22c55e; font-weight: bold;'
        )
        triggerNotificationVibration()

        setTickets(prevTickets => {
          const ticketIndex = prevTickets.findIndex(
            t => t.id === data.ticket.id
          )
          if (ticketIndex !== -1) {
            const updatedTickets = [...prevTickets]
            updatedTickets[ticketIndex] = data.ticket
            return updatedTickets
          }
          return [data.ticket, ...prevTickets]
        })

        const isFromCurrentUser = data.message?.senderId === user.id
        const shouldPlaySound = shouldPlayNotificationSound(data.ticket)

        if (!isFromCurrentUser && audioEnabledRef.current && shouldPlaySound) {
          try {
            if (isGroup) {
              console.log(
                '%c[AUDIO]%c Emitindo som: Nova mensagem em Grupo',
                LOG_THEMES.system,
                'color: #a855f7;'
              )
              soundGroupAlertRef.current()
            } else {
              console.log(
                '%c[AUDIO]%c Emitindo som: Nova mensagem em Atendimento Direto',
                LOG_THEMES.system,
                'color: #06b6d4;'
              )
              soundAttendanceAlertRef.current()
            }
          } catch (error) {
            console.warn('%c[AUDIO-ERROR]', LOG_THEMES.error, error)
          }
        } else {
          console.log(
            `%c[AUDIO]%c Bloqueado - Som omitido. Motivo: [Mensagem enviada por mim: ${isFromCurrentUser} | Áudio habilitado na ref: ${audioEnabledRef.current} | Permissão de som do ticket: ${shouldPlaySound}]`,
            LOG_THEMES.system,
            'color: #64748b;'
          )
        }
      } else {
        console.log(
          `%c[MESSAGE-NEW]%c Ignorada - Ticket ID: ${data.ticket?.id || 'N/A'}. Motivo do Descarte: [Mensagem Não-Lida: ${isNewUnreadMessage} | Status Aberto/Pendente: ${isTicketOpenOrPending} | Chatbot Ativo: ${isChatbot} | Pertence a Mim/Minha Fila: ${isForCurrentUser} | Se Grupo, Som Ativo: ${!isGroup || soundGroupNotifications}]`,
          LOG_THEMES.message,
          'color: #64748b;'
        )
      }
    }

    const handleTaskNotification = data => {
      if (data.action === 'create') {
        console.log(
          `%c[TASK]%c Nova tarefa criada ID: ${data.task?.id} - Adicionando ao painel e emitindo bip sonoro`,
          LOG_THEMES.system,
          'color: #eab308;'
        )
        triggerNotificationVibration()

        setTasks(prevTasks => {
          const taskExists = prevTasks.some(t => t.id === data.task.id)
          if (taskExists) return prevTasks
          return [data.task, ...prevTasks]
        })

        if (audioEnabledRef.current) {
          try {
            soundNotifyRef.current()
          } catch (error) {
            console.error('%c[AUDIO-ERROR]', LOG_THEMES.error, error)
          }
        }
      } else if (data.action === 'comment') {
        console.log(
          `%c[TASK-COMMENT]%c Novo comentário na tarefa ID: ${data.task?.id} - Atualizando estado e emitindo bip`,
          LOG_THEMES.system,
          'color: #eab308;'
        )
        triggerNotificationVibration()

        setTasks(prevTasks => {
          const existingIndex = prevTasks.findIndex(
            t => t.id === data.task.id && t._isComment
          )

          if (existingIndex >= 0) {
            const updated = [...prevTasks]
            updated[existingIndex] = {
              ...data.task,
              _isComment: true,
              _commentMessage: data.message
            }
            return updated
          }

          return [
            { ...data.task, _isComment: true, _commentMessage: data.message },
            ...prevTasks
          ]
        })

        if (audioEnabledRef.current) {
          try {
            soundNotifyRef.current()
          } catch (error) {
            console.error('%c[AUDIO-ERROR]', LOG_THEMES.error, error)
          }
        }
      }
    }

    const handleAnnouncementNotification = data => {
      if (data.action === 'update' || data.action === 'create') {
        const newAnnouncement = data.record
        triggerNotificationVibration()

        console.log(
          `%c[ANNOUNCEMENT]%c Comunicado Recebido: "${newAnnouncement.title}"`,
          LOG_THEMES.system,
          'color: #a855f7;'
        )

        if (newAnnouncement.isConnectionAnnouncement) {
          console.log(
            '%c[ANNOUNCEMENT]%c Identificado como aviso de Instância Desconectada. Avaliando abertura de modal...',
            LOG_THEMES.system,
            'color: #a855f7;'
          )
          if (location.pathname !== '/connections' && !loading) {
            const whatsAppName = newAnnouncement.title
              .replace('Conexão ', '')
              .replace(' Desconectada', '')
            const whatsApp = whatsApps.find(w => w.name === whatsAppName)

            const hiddenIds = user?.hiddenConnectionAnnouncementIds || []
            const isSilencedFromBackend = hiddenIds.includes(whatsApp?.id)
            const isSilencedFromStorage =
              localStorage.getItem(
                `doNotShowConnectionAnnouncement_${whatsApp?.id}`
              ) === 'true'
            const isSilenced = isSilencedFromBackend || isSilencedFromStorage

            if (whatsApp && !isSilenced && whatsApp.status !== 'CONNECTED') {
              console.log(
                `%c[ANNOUNCEMENT]%c Abrindo ConnectionModal para WhatsApp: ${whatsAppName}`,
                LOG_THEMES.system,
                'color: #9333ea;'
              )
              setConnectionAnnouncement(newAnnouncement)
              setSelectedWhatsAppId(whatsApp.id)
              setConnectionAnnouncementModalOpen(true)
            } else {
              setConnectionAnnouncementModalOpen(false)
            }
          }
          return
        } else {
          setAnnouncements(prevAnnouncements => {
            const updatedAnnouncements = [...prevAnnouncements]
            const annIndex = updatedAnnouncements.findIndex(
              ann => ann.id === newAnnouncement.id
            )

            if (annIndex !== -1) {
              updatedAnnouncements[annIndex] = newAnnouncement
            } else {
              updatedAnnouncements.unshift(newAnnouncement)
            }

            if (audioEnabledRef.current) {
              try {
                soundNotifyRef.current()
              } catch (error) {
                console.error('%c[AUDIO-ERROR]', LOG_THEMES.error, error)
              }
            }
            return updatedAnnouncements
          })
        }
      } else if (data.action === 'delete') {
        console.log(
          `%c[ANNOUNCEMENT]%c Remoção solicitada para ID: ${data.id}`,
          LOG_THEMES.system,
          'color: #64748b;'
        )
        if (connectionAnnouncement && connectionAnnouncement.id === data.id) {
          // A preferência "não exibir novamente" agora é persistida no backend
          // e deve ser mantida mesmo quando o anúncio é removido
          setConnectionAnnouncement(null)
          setConnectionAnnouncementModalOpen(false)
        }
        setAnnouncements(prevAnnouncements =>
          prevAnnouncements.filter(ann => ann.id !== data.id)
        )
      }
    }

    socket.on(`company-${companyId}-ticket`, handleTicketNotification)
    socket.on(`company-${companyId}-appMessage`, handleMessageNotification)
    socket.on(`company-${companyId}-task`, handleTaskNotification)
    socket.on('company-announcement', handleAnnouncementNotification)
    window.addEventListener(
      'pending-ticket-notification',
      handleLocalPendingTicketNotification
    )

    socket.on(`company-${companyId}-whatsapp`, data => {
      if (data.action === 'update' && data.whatsapp) {
        console.log(
          `%c[WHATSAPP-AUDIT]%c Mudança de estado de conexão -> ${data.whatsapp.name} está ${data.whatsapp.status}`,
          LOG_THEMES.system,
          'color: #94a3b8;'
        )
      }
    })

    return () => {
      window.removeEventListener(
        'pending-ticket-notification',
        handleLocalPendingTicketNotification
      )
      socket.disconnect()
    }
  }, [
    connectionAnnouncement,
    getPendingNotificationKey,
    loading,
    notificationEndTime,
    notificationStartTime,
    socketManager,
    soundGroupNotifications,
    user,
    triggerNotificationVibration,
    whatsApps,
    location,
    isUserInTicketQueue
  ])

  useEffect(() => {
    const calculateUnreadCount = () => {
      const announcementUnread = announcements.length
      const ticketUnread = tickets.length
      const taskUnread = tasks.length
      const chatUnread = chats.length
      setTotalUnreadCount(
        announcementUnread + ticketUnread + taskUnread + chatUnread
      )
    }
    calculateUnreadCount()
  }, [chats, announcements, tickets, tasks, user.id])

  const handleOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // const handleAnnouncementRead = announcementId => {
  //   saveAnnouncementToLocalStorage(announcementId, user.id)
  //   setAnnouncements(prevAnnouncements =>
  //     prevAnnouncements.filter(
  //       announcement => announcement.id !== announcementId
  //     )
  //   )
  // }

  const handleAnnouncementRead = async announcementId => {
    saveAnnouncementToLocalStorage(announcementId, user.id)

    // Se for um anúncio de alerta de armazenamento, desativa-o no banco de dados para que não reapareça em novos logins
    const targetAnnouncement = announcements.find(
      ann => ann.id === announcementId
    )
    if (targetAnnouncement && targetAnnouncement.tipo === 'storage-alert') {
      try {
        await api.put(`/announcements/${announcementId}`, {
          ...targetAnnouncement,
          status: false
        })
      } catch (err) {
        console.error('Error disabling storage announcement in database:', err)
      }
    }

    setAnnouncements(prevAnnouncements =>
      prevAnnouncements.filter(
        announcement => announcement.id !== announcementId
      )
    )
  }

  const handleTaskRead = taskId => {
    setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId))
  }

  const handleStorageAlertClick = useCallback(taskId => {
    setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId))
    // Opcional: toast.info("Notificação removida");
  }, [])

  const handleTaskClick = task => {
    if (task._isStorageAlert) {
      handleStorageAlertClick(task.id)
      return // Encerra aqui para NÃO abrir nenhum modal
    }

    // Se for uma notificação de comentário, abre o modal de comentários
    if (task._isComment) {
      setSelectedTaskForComments(task)
      setTaskCommentsModalOpen(true)
    } else {
      // Se for uma notificação de tarefa normal, abre o modal da tarefa
      setSelectedTaskId(task.id)
      setTaskModalOpen(true)
    }
  }

  const handleCloseTaskModal = () => {
    setTaskModalOpen(false)
    setSelectedTaskId(null)
  }

  const handleCloseTaskCommentsModal = () => {
    setTaskCommentsModalOpen(false)
    setSelectedTaskForComments(null)
  }

  const handleCloseConnectionAnnouncementModal = () => {
    setConnectionAnnouncementModalOpen(false)
  }

  const handleRequestNewQrCode = async whatsAppId => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`)
      setSelectedWhatsAppId(whatsAppId)
      setQrModalOpen(true)
    } catch (err) {
      toastError(err)
    }
  }

  const handleCloseQrModal = () => {
    setQrModalOpen(false)
    setSelectedWhatsAppId(null)
  }

  const handleOpenQrModal = () => {
    if (connectionAnnouncement && !loading) {
      const whatsAppName = connectionAnnouncement.title
        .replace('Conexão ', '')
        .replace(' Desconectada', '')
      const whatsApp = whatsApps.find(w => w.name === whatsAppName)
      if (whatsApp) {
        handleRequestNewQrCode(whatsApp.id)
        setConnectionAnnouncementModalOpen(false)
      }
    }
  }

  return (
    <div>
      <IconButton onClick={handleOpen} className={classes.notificationButton}>
        <Badge
          badgeContent={totalUnreadCount}
          className={classes.badgeCountTotal}
          overlap="rectangular"
        >
          <BellIcon />
        </Badge>
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <Paper className={classes.mainPaper}>
          <div className={classes.notificationType}>
            <NotificationsPopOver
              notifications={tickets}
              volume={volume}
              onClose={handleClose}
            />

            {chats.length > 0 && (
              <ChatPopover
                notifications={chats}
                user={user}
                handleChatOpen={handleChatOpen}
                onClose={handleClose}
                markChatAsRead={chatId => {
                  setChats(prev => prev.filter(c => c.id !== chatId))
                }}
              />
            )}

            {tasks.length > 0 && (
              <TasksPopOver
                tasks={tasks}
                onClose={handleClose}
                onTaskRead={handleTaskRead}
                onTaskClick={handleTaskClick}
              />
            )}

            <AnnouncementsPopover
              notifications={announcements}
              savedAnnouncements={savedAnnouncements}
              onAnnouncementRead={handleAnnouncementRead}
            />

            {totalUnreadCount === 0 && (
              <List>
                <ListItem>
                  <ListItemText
                    primary="Nenhuma notificação disponível."
                    className={classes.noNotifications}
                  />
                </ListItem>
              </List>
            )}
          </div>
        </Paper>
      </Popover>
      <ConnectionAnnouncementModal
        open={connectionAnnouncementModalOpen}
        onClose={handleCloseConnectionAnnouncementModal}
        announcement={connectionAnnouncement}
        onOpenQrModal={handleOpenQrModal}
        whatsAppId={selectedWhatsAppId}
      />
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={selectedWhatsAppId}
      />
      <TaskModal
        open={taskModalOpen}
        onClose={handleCloseTaskModal}
        taskId={selectedTaskId}
        reload={() => {}}
      />
      <TaskCommentsModal
        open={taskCommentsModalOpen}
        onClose={handleCloseTaskCommentsModal}
        task={selectedTaskForComments}
      />
    </div>
  )
}
